import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Box, Checkbox, Container, FormControlLabel, Stack } from "@mui/material"

import LoadingButton from "widgets/common/LoadingButton"
import TermsAndConditionsContent from "widgets/user/TermsAndConditionsContent"
import { selectCurrentUser } from "features/store/authSlice"
import { useAcceptUserTermsMutation } from "features/api"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import {
  DEFAULT_TARGET,
  PREVIOUS_LOCATION_KEY,
  SECTIONS_BORDER_RADIUS,
} from "helpers/utils/constants"
import useDocumentTitle from "helpers/hooks/useDocumentTitle"

interface TermsAndConditionsProps {
  actionable?: boolean
}

const TermsAndConditions = ({ actionable = false }: TermsAndConditionsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  useDocumentTitle(t("pageTitles.TERMS_AND_CONDITIONS"))
  const username = useSelector(selectCurrentUser)
  const [readAndAgreed, setReadAndAgreed] = useState(false)
  const [acceptUserTerms, { isLoading }] = useAcceptUserTermsMutation()

  const handleAccept = async () => {
    await snackbarMutation({
      mutation: acceptUserTerms({ username: String(username) }).unwrap(),
      getErrorMessage: buildGetErrorMessage(
        t("error.UPDATING_ITEM", {
          item: t("generic.TERMS_AND_CONDITIONS").toLowerCase(),
        }),
      ),
      getSuccessMessage: () =>
        t("success.UPDATING_ITEM", {
          item: t("generic.TERMS_AND_CONDITIONS"),
          count: 2,
          context: "male",
        }),
    })
      .then(() => {
        const target = sessionStorage.getItem(PREVIOUS_LOCATION_KEY) ?? DEFAULT_TARGET
        navigate(target)
      })
      .catch(() => {})
  }

  return actionable ? (
    <Box
      height={"100vh"}
      display={"flex"}
      flexDirection={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      px={2}
    >
      <Container
        maxWidth="md"
        sx={{
          py: "2rem",
          maxHeight: "60vh",
          overflow: "auto",
          bgcolor: "background.paper",
          border: (theme) => `1px solid ${theme.palette.neutral[400]}`,
          borderRadius: SECTIONS_BORDER_RADIUS,
          boxShadow: (theme) => `0 0 4px 1px ${theme.palette.neutral[100]} inset`,
        }}
      >
        <TermsAndConditionsContent />
      </Container>
      <Stack pt={2} gap={2} alignItems={"center"}>
        <FormControlLabel
          label={t("terms_and_conditions.READ_AND_AGREED")}
          control={
            <Checkbox
              size="medium"
              checked={readAndAgreed}
              onChange={() => setReadAndAgreed(!readAndAgreed)}
            />
          }
        />
        <LoadingButton
          loading={isLoading}
          variant="contained"
          disabled={!readAndAgreed}
          onClick={handleAccept}
        >
          {t("terms_and_conditions.CONTINUE")}
        </LoadingButton>
      </Stack>
    </Box>
  ) : (
    <Container maxWidth="lg" sx={{ py: "3rem" }}>
      <TermsAndConditionsContent />
    </Container>
  )
}

export default TermsAndConditions
